import { SimpleProduct } from "../../types/prodcatBaseTypes";
import { noticeError } from "../../utils/newrelic/requests";
import {
  SkinMatchApiResponse,
  SkinMatchIngredientData,
  SkinMatchResponseSchema,
} from "./skinMatchSchema";

type NoAction = {
  status: "No Action";
};
type Loading = {
  status: "Loading";
};

type Error = {
  status: "Error";
  message: string;
};

type Success<DataType> = {
  status: "Done";
  data: DataType;
};
type LoadData<DataType> = NoAction | Loading | Error | Success<DataType>;

type EANStatus = {
  url: string;
  skinMatchStatus: boolean;
};

type LogOptions = {
  ean: string;
  productId: SimpleProduct["aliases"]["pimProductId"];
  variantId: SimpleProduct["id"];
};

type SkinMatchStatus = {
  status: {
    code: "200" | "204" | "400" | "404";
    type: string;
    message: string;
  };
};

type SkinMatchIngredientBaseUrl = `${string}&ean=${string}`;

const skinMatchIngredientURL = (basePath: string): SkinMatchIngredientBaseUrl =>
  `${basePath}/json.php?id=6284d04c901f0&ean=`;

function getSkinMatchIngredientURL(
  ean: string,
  skinMatchIngredientBasePath: string,
) {
  return `https://${skinMatchIngredientURL(skinMatchIngredientBasePath)}${ean}`;
}

async function getSkinMatchIngredientsData(
  url: string,
): Promise<SkinMatchIngredientData> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error("An error occurred while fetching the data.");
  }
  const skinMatchIngredientsData: SkinMatchApiResponse =
    SkinMatchResponseSchema.parse(await res.json());
  if ("status" in skinMatchIngredientsData) {
    throw new Error(skinMatchIngredientsData.reason);
  }
  return skinMatchIngredientsData;
}

async function checkEANStatus(
  url: string,
  logOptions: LogOptions,
): Promise<EANStatus> {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(res.statusText);
    }
    const skinMatchStatus: SkinMatchStatus = await res.json();
    return {
      skinMatchStatus: skinMatchStatus.status.code === "200",
      url,
    };
  } catch (e) {
    const { ean } = logOptions;
    noticeError(`Current EAN: ${ean} not supported`, logOptions);
    return { url, skinMatchStatus: false };
  }
}

export {
  checkEANStatus,
  getSkinMatchIngredientURL,
  getSkinMatchIngredientsData,
};
export type { LoadData, EANStatus, LogOptions };
